import TariffQuoteParser from "@soenergy/frontend-library/src/services/Quote/TariffQuoteParser"
import TariffQuoteParserV2 from "@soenergy/frontend-library/src/services/Quote/TariffQuoteParserV2"
import TrackingService from "@soenergy/frontend-library/src/services/TrackingService"
import ErrorReporter from "@soenergy/frontend-library/src/services/ErrorReporter"
import {
  UsageType,
  UsagePeriod
} from "@soenergy/frontend-library/src/services/Quote/QuoteFormConstants"

import Referral from "@/services/Referral.js"
import Vue from "vue"
import EvEligibilityService from "../services/quote/EvEligibilityService"
import { SmartMeterEvTariffEligibilityErrors } from "../cmsVariables/quote"

const getInitialState = () => ({
  tariffQuotes: null,
  tariffFetchError: null,
  referralDetails: null,
  selectedQuote: null,
  quoteSummaryContent: null,
  isContentFetched: false,
  furthestCompletedStep: 0,
  invalidQuoteTariffName: null,
  meterEvEligibilityError: null,

  form: {
    postcode: null,
    fuel: null,
    meterType: null,
    isMeterMultirate: null,
    supplyAddress: null,
    supplyAddressGas: null,
    estimatedUsage: null,
    exactAmountGas: { usage: null, period: UsagePeriod.Year },
    exactAmountElec: { usage: null, period: UsagePeriod.Year },
    seasonalPayments: null,
    bankAccountHolderName: null,
    bankAccountNumber: null,
    sortCode: null,
    paymentDay: null,
    billingAddress: null,
    isBillingAddressSameAsSupply: true,
    title: null,
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    dateOfBirth: null,
    priorityServiceRegister: null,
    marketingOptIn: null,

    hasSelectedEv: null,
    evType: null,
    hasValidEv: null,
    carRegistration: null,
    isConsideringGettingEv: false,
    hasMeterEligibleForEvTariff: false
  }
})

export const state = () => getInitialState()

export const mutations = {
  UPDATE_FORM(state, form) {
    state.form = { ...state.form, ...form }
  },
  RESET_FORM(state) {
    Object.assign(state, getInitialState())
  },
  SET_TARIFF_QUOTES(state, tariffQuotes) {
    state.tariffQuotes = tariffQuotes
  },
  SET_INVALID_TARIFF(state, invalidQuoteTariffName) {
    state.invalidQuoteTariffName = invalidQuoteTariffName
  },
  SET_TARIFF_FETCH_ERROR(state, error) {
    state.tariffFetchError = error
  },
  SET_REFERRAL_DETAILS(state, referralDetails) {
    state.referralDetails = referralDetails
  },
  SET_QUOTE(state, quote) {
    state.selectedQuote = quote
  },
  SET_QUOTE_SUMMARY(state, quoteSummaryContent) {
    state.quoteSummaryContent = quoteSummaryContent
  },
  SET_CONTENT_FETCHED_FLAG(state, isContentFetched) {
    state.isContentFetched = isContentFetched
  },
  UPDATE_FURTHEST_STEP_COMPLETED(state, furthestCompletedStep) {
    state.furthestCompletedStep = furthestCompletedStep
  },
  UPDATE_MPAN(state, mpan) {
    state.form.supplyAddress.mpan = mpan
  },
  UPDATE_MPRN(state, mprn) {
    state.form.supplyAddressGas.mprn = mprn
  },
  SET_METER_ELIGIBILITY_FOR_EV_TARIFF(state, hasMeterEligibleForEvTariff) {
    state.form.hasMeterEligibleForEvTariff = hasMeterEligibleForEvTariff
  },
  SET_METER_EV_TARIFF_ELIGIBILITY_ERROR(state, meterEvEligibilityError) {
    state.meterEvEligibilityError = meterEvEligibilityError
  },
  SET_IS_METER_MULTIRATE(state, isMeterMultirate) {
    state.form.isMeterMultirate = isMeterMultirate
  },
  SET_ESTIMATED_USAGE(state, estimatedUsage) {
    state.form.estimatedUsage = estimatedUsage
  }
}

export const actions = {
  async fetchTariffQuotes({ state, commit }) {
    commit("SET_TARIFF_FETCH_ERROR", null)

    // change quote endpoint based on env
    const useQuoteV2Endpoint =
      process.env.VUE_APP_USE_NEW_QUOTES_ENDPOINT === "true"
    const TariffParser = useQuoteV2Endpoint
      ? TariffQuoteParserV2
      : TariffQuoteParser

    try {
      const { quotes, invalidQuoteTariffName } = await TariffParser.getQuotes(
        state.form
      )
      commit("SET_TARIFF_QUOTES", quotes)
      commit("SET_INVALID_TARIFF", invalidQuoteTariffName)
    } catch (error) {
      commit("SET_TARIFF_FETCH_ERROR", error)
      throw error
    }
  },
  async ensureReferralDetailsFetched({ state, commit }) {
    const code = Vue.$cookies.get("voucher")
    const isPromoCode = Vue.$cookies.get("promotional_code_used") === "true"

    if (!code || isPromoCode || state.referralDetails) {
      return
    }
    try {
      const { data } = await Referral.checkVoucher(code)
      commit("SET_REFERRAL_DETAILS", {
        credit: data.current_referral_fee,
        referrer: data.referrer,
        code
      })
      TrackingService.sendEvent("voucherDetailsAppliedToQuoteProcess")
    } catch (e) {
      console.error(e)
      ErrorReporter.report(e)
    }
  },
  async fetchLayoutContent({ state, commit }) {
    if (state.isContentFetched) return

    const [{ story: quoteSummary }] = await Promise.all([
      this.$storyblokHelper.get({
        id: "website/quote/summary"
      })
    ])

    commit("SET_QUOTE_SUMMARY", quoteSummary.content)
    commit("SET_CONTENT_FETCHED_FLAG", true)
  },

  setPostcodeAndReset({ commit }, postcode) {
    commit("RESET_FORM")
    commit("UPDATE_FORM", { postcode: postcode.toUpperCase() })
  },

  async checkMeterEligibilityForEvTariff({ state, commit }) {
    // checks whether mpan entered manually or not
    const mpan = state.form.supplyAddress.isEnteredManually
      ? state.form.mpan
      : state.form.supplyAddress.mpan

    try {
      const data = await EvEligibilityService.verifySmartMeter(mpan)

      commit("SET_METER_ELIGIBILITY_FOR_EV_TARIFF", !!data.isEligible)
      commit("SET_METER_EV_TARIFF_ELIGIBILITY_ERROR", data.errorCode)

      if (data.isEligible && state.form.isMeterMultirate) {
        // Correct meter type and usage with EES API
        commit("SET_IS_METER_MULTIRATE", false)
        if (state.form.estimatedUsage === UsageType.low) {
          commit("SET_ESTIMATED_USAGE", UsageType.medium)
        }
      }
    } catch (error) {
      console.error(error)
      ErrorReporter.report(error)
      commit(
        "SET_METER_EV_TARIFF_ELIGIBILITY_ERROR",
        SmartMeterEvTariffEligibilityErrors.UNKNOWN
      )
    }
  }
}

export const getters = {}
